import { ORDER_NAME } from '@/app/constants/calculator';
import { createParsedKey } from '@/app/lcv/components/sections/compare/utils';
import { DEFAULT_MODEL_YEAR } from '@/services/lineup-model/lcv-product-compare/const';
import type {
  CalculatorModelsFromLineupResult,
  CategoryDefaultGrades,
  DefaultModelGrade,
  LcvModelConfigWithLineup,
  LcvModelWithFieldKey,
  LcvYearLineupModel,
} from '@/services/lineup-model/lcv-product-compare/types';
import type {
  LcvModel,
  LcvModelCategory,
  LcvModelColor,
} from '@/types/tis-cms/types';

export const getLcvCategoryKey = (
  category: Pick<LcvModelCategory, 'title' | 'documentId'>
) => category.title ?? category.documentId;

export const buildCategoryModelKey = (documentId: string, modelYear: string) =>
  `${documentId}_${modelYear}`;

export const getLcvModelColors = (model: LcvModel): LcvModelColor[] =>
  (model.colors ?? []).filter((color): color is LcvModelColor => !!color);

export const getLcvModelDefaultColor = (
  model: LcvModel
): LcvModelColor | undefined =>
  model.defaultColor ?? getLcvModelColors(model)[0];

export const attachFieldKeyToModel = (
  model: LcvModel
): LcvModelWithFieldKey => ({
  ...model,
  fieldKey: createParsedKey({ title: model.title }),
});

export const getActiveYearLineup = (
  yearLineupModels?: LcvModelConfigWithLineup['yearLineupModels']
): LcvYearLineupModel | undefined =>
  (yearLineupModels ?? []).find(
    (item): item is LcvYearLineupModel => item?.modelYear === DEFAULT_MODEL_YEAR
  ) ??
  (yearLineupModels ?? []).find(
    (item): item is LcvYearLineupModel => !!item?.active
  ) ??
  (yearLineupModels ?? [])[0] ??
  undefined;

export const shouldSkipSparkModel = (
  model: Pick<LcvModel, 'hideFromCustomize'>
) => !!model.hideFromCustomize;

const resolveSubCategoryDefaultModelId = (
  models: LcvModel[],
  subCategoryId: string,
  configuredDefaultId?: string
): string | undefined => {
  const subCategoryModels = models.filter(
    (model) => model.subCategory?.documentId === subCategoryId
  );

  if (configuredDefaultId) {
    const configuredModel = subCategoryModels.find(
      (model) => model.documentId === configuredDefaultId
    );
    if (configuredModel) return configuredDefaultId;
  }

  return subCategoryModels[0]?.documentId;
};

export const extractDefaultGrades = (
  defaultModelGrade: DefaultModelGrade,
  models: LcvModel[]
): CategoryDefaultGrades => {
  const defaultModelsBySubCategory: Record<string, string> = {};
  let defaultModelDocumentId: string | undefined;

  if (defaultModelGrade?.defaultModel?.documentId) {
    defaultModelDocumentId = defaultModelGrade.defaultModel.documentId;
  }

  for (const subCategoryItem of defaultModelGrade?.subCategory ?? []) {
    if (!subCategoryItem) continue;

    const subCategoryId = subCategoryItem.subCategory?.documentId;
    if (!subCategoryId) continue;

    const resolvedDefaultId = resolveSubCategoryDefaultModelId(
      models,
      subCategoryId,
      subCategoryItem.defaultSubModel?.documentId
    );

    if (resolvedDefaultId) {
      defaultModelsBySubCategory[subCategoryId] = resolvedDefaultId;
    }
  }

  return { defaultModelDocumentId, defaultModelsBySubCategory };
};

export const resolveDefaultSubModelBySubCategorySlug = (
  defaultModelGrade: DefaultModelGrade,
  slug: string
): string | undefined =>
  defaultModelGrade?.subCategory?.find((item) =>
    item?.subCategory?.title?.toLowerCase().includes(slug)
  )?.defaultSubModel?.documentId;

export const getMuxCustomizeSwitchItems = (
  models: LcvModel[],
  defaultModelGrade: DefaultModelGrade
): [string, string] => [
  resolveDefaultSubModelBySubCategorySlug(defaultModelGrade, 'rs') ??
    models[0]?.documentId ??
    '',
  resolveDefaultSubModelBySubCategorySlug(defaultModelGrade, 'ultimate') ??
    models[0]?.documentId ??
    '',
];

export const getXSeriesCustomizeSwitchItems = (
  models: LcvModel[],
  defaultModelGrade: DefaultModelGrade
): [string, string] => [
  resolveDefaultSubModelBySubCategorySlug(defaultModelGrade, 'speed') ??
    models[0]?.documentId ??
    '',
  resolveDefaultSubModelBySubCategorySlug(defaultModelGrade, 'hi-lander') ??
    models[0]?.documentId ??
    '',
];

export const buildCalculatorModelsFromLineup = (
  activeYearLineup: LcvYearLineupModel | undefined
): CalculatorModelsFromLineupResult => {
  const modelYear = activeYearLineup?.modelYear ?? '';
  const lineupModels = (activeYearLineup?.lineupModels ?? []).filter(
    (item) => !!item?.category
  );

  const parseModelCategories: LcvModelCategory[] = [];
  const mappedModelItemsByCategoryUid: Record<string, LcvModelWithFieldKey[]> =
    {};
  const defaultGradesByCategoryKey: Record<string, CategoryDefaultGrades> = {};

  for (const lineupItem of lineupModels) {
    if (!lineupItem?.category) continue;

    const category = lineupItem.category;
    parseModelCategories.push(category);

    const strapiModels = (lineupItem.models ?? []).filter(
      (model): model is LcvModel => !!model
    );

    const modelsWithFieldKey = strapiModels.map(attachFieldKeyToModel);

    const categoryKey = buildCategoryModelKey(category.documentId, modelYear);
    mappedModelItemsByCategoryUid[categoryKey] = modelsWithFieldKey;

    defaultGradesByCategoryKey[categoryKey] = extractDefaultGrades(
      lineupItem.defaultModelGrade,
      strapiModels
    );
  }

  parseModelCategories.sort(
    (a, b) =>
      ORDER_NAME.indexOf(a.nameTh ?? '') - ORDER_NAME.indexOf(b.nameTh ?? '')
  );

  return {
    modelYear,
    parseModelCategories,
    mappedModelItemsByCategoryUid,
    defaultGradesByCategoryKey,
  };
};
